import { ListingResponse } from '@/api/types';
import { client } from '@/client';
import {
  AttemptsReport,
  CourseCompletionReportsType,
  DepartmentPerformanceReport,
  GroupedByEntityFilterType,
  KeyStatsReport,
  PerformanceOvertimeReport,
  PhishingSignReports,
  ReportRequest,
  SimulationCampaignsReport,
  userAwarenessReportsType,
  VulnerableUsersOverTimeReport,
  VulnerableUsersReport,
} from '@/client/reports';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';

const queryKeyCourseCompletion = 'courseCompletionReports';
const queryKeyUserAwareness = 'userAwarenessReports';

export const useCourseCompletionReports = (
  params: UseQueryOptions & {
    accountId?: string;
  },
): UseQueryResult<CourseCompletionReportsType> & {
  courseCompletionReports: CourseCompletionReportsType | undefined;
} => {
  const {
    accountId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKeyCourseCompletion, accountId],
    () =>
      accountId
        ? client.reports.getCourseCompletionReports(accountId)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<CourseCompletionReportsType>),
    },
  );
  return {
    courseCompletionReports: data,
    ...(rest as UseQueryResult<CourseCompletionReportsType>),
  };
};

export const useUserAwarenessReports = (
  params: UseQueryOptions & {
    accountId?: string;
  },
): UseQueryResult<userAwarenessReportsType> & {
  userAwarenessReports: userAwarenessReportsType | undefined;
} => {
  const {
    accountId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKeyUserAwareness, accountId],
    () =>
      accountId ? client.reports.getUserAwarenessReports(accountId) : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<userAwarenessReportsType>),
    },
  );

  return {
    userAwarenessReports: data,
    ...(rest as UseQueryResult<userAwarenessReportsType>),
  };
};

export const useCourseAttemptsReports = (
  params: UseQueryOptions & {
    courseId?: string;
  },
): UseQueryResult<AttemptsReport[]> & {
  attemptStats: AttemptsReport[] | undefined;
} => {
  const {
    courseId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['courseAttemptsReports', courseId],
    () =>
      courseId ? client.reports.getCourseAttemptsReport(courseId) : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<AttemptsReport[]>),
    },
  );

  return {
    attemptStats: data,
    ...(rest as UseQueryResult<AttemptsReport[]>),
  };
};

export const usePerformanceOvertimeReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<PerformanceOvertimeReport[]> & {
  performanceOvertime: PerformanceOvertimeReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['performanceOvertimeReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getPerformanceOvertimeReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    performanceOvertime: data,
    ...(rest as UseQueryResult<PerformanceOvertimeReport[]>),
  };
};

export const useKeyStatsReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<KeyStatsReport> & {
  keyStats: KeyStatsReport | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['keyStatsReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getKeyStatsReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    keyStats: data,
    ...(rest as UseQueryResult<KeyStatsReport>),
  };
};

export const usePhishingSignReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<PhishingSignReports[]> & {
  phishingSigns: PhishingSignReports[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['phishingSignReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getPhishingSignReports(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    phishingSigns: data,
    ...(rest as UseQueryResult<PhishingSignReports[]>),
  };
};

export const useDepartmentPerformanceReports = (
  accountId: string,
  params: UseQueryOptions &
    ReportRequest & {
      groupBy: GroupedByEntityFilterType;
    },
): UseQueryResult<DepartmentPerformanceReport[]> & {
  departmentPerformance: DepartmentPerformanceReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['departmentPerformanceReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getDepartmentPerformanceReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    departmentPerformance: data,
    ...(rest as UseQueryResult<DepartmentPerformanceReport[]>),
  };
};

export const useSimulationCampaignsReports = (
  accountId: string,
  params: UseQueryOptions &
    ReportRequest & {
      sort?: string[];
    } = {},
): UseQueryResult<SimulationCampaignsReport[]> & {
  simulationCampaigns: SimulationCampaignsReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    sort,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      'simulationCampaignsReports',
      accountId,
      JSON.stringify(sort),
      JSON.stringify(params),
    ],
    () =>
      accountId
        ? client.reports.getSimulationCampaignsReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    simulationCampaigns: data,
    ...(rest as UseQueryResult<SimulationCampaignsReport[]>),
  };
};

export const useVulnerableUsersReports = (
  accountId: string,
  params: UseQueryOptions &
    ReportRequest & {
      sort?: string[];
      take?: number;
      skip?: number;
    } = {},
): UseQueryResult<ListingResponse<VulnerableUsersReport>> & {
  vulnerableUsers: VulnerableUsersReport[] | undefined;
  total?: number | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['vulnerableUsersReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getVulnerableUsersReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    vulnerableUsers: data?.result,
    total: data?.count,
    ...(rest as UseQueryResult<ListingResponse<VulnerableUsersReport>>),
  };
};

export const useVulnerableUsersOverTimeReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<VulnerableUsersOverTimeReport[]> & {
  vulnerableUsersOverTime: VulnerableUsersOverTimeReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['vulnerableUsersOverTimeReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.reports.getVulnerableUsersOverTimeReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    vulnerableUsersOverTime: data,
    ...(rest as UseQueryResult<VulnerableUsersOverTimeReport[]>),
  };
};
